import { TStore } from "@components/types";
import appConfig from "@core/appconfig";
import { formatAddress } from "@core/utils";
import { Mail, MapPin, Phone } from "lucide-react";
import Link from "next/link";
import { FaWhatsappSquare } from "react-icons/fa";

interface HotelFooterProps {
    store: TStore;
}

const FOOTER_BG = '#0B3B2E';
const ACCENT = '#5FD1AE';

// Single branded entity per page (Google Hotel Ads): the footer, the booking
// flow and the policies all name ONE seller.
//  - settings.branded === true  -> the property's own identity and contact.
//  - otherwise                  -> Jhattse (brand) operated by Esangrah Technologies
//                                  Pvt. Ltd. (legal entity), with the property shown
//                                  as the place being booked.
export const HotelFooter = ({ store }: HotelFooterProps) => {
    const settings = store?.storemeta?.settings;
    const branded = settings?.branded === true;
    const hideContact = settings?.hide_contact;
    const hideEmail = settings?.hide_email;
    const phone = store?.phone;
    const storeEmail = store?.email || settings?.order_email;
    const address = formatAddress(store?.address);
    const policyBaseHref = `/stays/${store?.id}`;
    const year = new Date().getFullYear();

    const BRAND = appConfig.appName || 'Jhattse';
    const LEGAL = 'Esangrah Technologies Pvt. Ltd.';
    const CIN = appConfig.cin || 'U72900UR2023PTC015183';
    const GSTIN = appConfig.gstin || '05AAHCE4252A1Z7';
    const REG_ADDR = appConfig.registeredAddress || 'Natthanpur, Dehradun, Uttarakhand 248005';
    const PLATFORM_EMAIL = 'connect@jhattse.com';

    // Contact shown: the store's own when branded, platform's otherwise.
    const showPhone = branded && !hideContact && !!phone;
    const showEmail = branded ? (!hideEmail && !!storeEmail) : true;
    const contactEmail = branded ? storeEmail : PLATFORM_EMAIL;

    const linkCls = "!text-white/70 hover:!text-white text-sm font-medium transition-colors";
    const headCls = "text-xs font-semibold text-white/50 uppercase tracking-wider select-none";
    const contactCls = "flex flex-row items-center gap-2 text-sm font-medium !text-white/85 hover:!text-white transition-colors max-w-full break-all";

    return (
        <footer className="flex flex-col justify-center w-full font-manrope text-white" style={{ backgroundColor: FOOTER_BG }}>
            <div className="flex flex-col md:flex-row md:flex-wrap box-border md:justify-between gap-6 p-5 md:p-8 max-w-[1240px] w-full mx-auto">
                {/* Identity */}
                <div className="flex flex-col justify-start gap-2 max-w-sm min-w-0">
                    {branded ? (
                        <>
                            <div className="text-white font-bold text-xl break-words">{store?.name}</div>
                            {store?.legal_name && store.legal_name !== store?.name && (
                                <div className="text-white/60 text-xs">{store.legal_name}</div>
                            )}
                            {address && (
                                <div className="text-white/70 text-sm flex items-start gap-1.5 min-w-0">
                                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
                                    <span className="break-words">{address}</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="text-white font-bold text-xl">{BRAND}</div>
                            <div className="text-white/70 text-sm">
                                Operated by <span className="font-semibold text-white">{LEGAL}</span>
                            </div>
                            <div className="text-white/60 text-xs leading-relaxed">
                                <div>CIN: {CIN}</div>
                                <div>GSTIN: {GSTIN}</div>
                                <div className="flex items-start gap-1.5 mt-1">
                                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: ACCENT }} />
                                    <span>Registered office: {REG_ADDR}</span>
                                </div>
                            </div>
                            {store?.name && (
                                <div className="mt-2 pt-2 border-t border-white/10 text-white/70 text-xs min-w-0">
                                    <div className="uppercase tracking-wider text-white/40 font-semibold mb-0.5">Property</div>
                                    <div className="text-white/90 text-sm font-semibold break-words">{store.name}</div>
                                    {address && <div className="break-words">{address}</div>}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row md:justify-end gap-6 md:gap-12">
                    {/* Links */}
                    <div className="flex flex-col gap-1.5">
                        <div className={headCls}>Useful links</div>
                        <div className="grid grid-cols-2 sm:flex sm:flex-col gap-2 sm:gap-1.5 sm:w-44">
                            <Link href="/stays" prefetch={false} className={linkCls}>All Hotels &amp; Stays</Link>
                            {!branded && <Link href="/stays/about" prefetch={false} className={linkCls}>About {BRAND}</Link>}
                            <Link href={`${policyBaseHref}/privacy`} prefetch={false} className={linkCls}>Privacy Policy</Link>
                            <Link href={`${policyBaseHref}/terms`} prefetch={false} className={linkCls}>Terms of Use</Link>
                            <Link href={`${policyBaseHref}/refund`} prefetch={false} className={linkCls}>Refund Policy</Link>
                        </div>
                    </div>

                    {/* Contact */}
                    <div className="flex flex-col gap-2 min-w-0">
                        <div className={headCls}>Get in touch</div>
                        <div className="flex flex-col md:items-end gap-2 min-w-0 w-full">
                            {showPhone && (
                                <Link href={`tel:${phone}`} className={contactCls}>
                                    <Phone size="1em" className="flex-shrink-0" style={{ color: ACCENT }} />{phone}
                                </Link>
                            )}
                            {showEmail && contactEmail && (
                                <Link href={`mailto:${contactEmail}`} className={contactCls}>
                                    <Mail size="1em" className="flex-shrink-0" style={{ color: ACCENT }} />{contactEmail}
                                </Link>
                            )}
                            {showPhone && (
                                <Link
                                    href={`https://wa.me/${phone.replace(/\D/g, "")}?text=Hello%2C%20I%20have%20a%20query%20about%20my%20booking.`}
                                    className={contactCls}
                                >
                                    <FaWhatsappSquare size="1.15em" className="flex-shrink-0" style={{ color: ACCENT }} />{phone}
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="text-white/40 text-center text-xs min-h-10 py-2 px-4 w-full box-border flex flex-col justify-center select-none border-t border-white/10">
                {branded
                    ? `© ${year} ${store?.legal_name || store?.name}. All rights reserved.`
                    : `© ${year} ${LEGAL} · ${BRAND} is a brand of ${LEGAL}`}
            </div>
        </footer>
    );
};
